import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import "moment/locale/es";
import * as XLSX from "xlsx";
import {
  FaSearch,
  FaFilter,
  FaFileExcel,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaExclamationTriangle,
  FaWallet,
  FaHistory,
  FaTimes,
  FaCreditCard,
  FaCalendarAlt,
  FaTimesCircle,
  FaCheckCircle,
  FaExclamationCircle,
  FaAddressBook,
  FaPhoneAlt,
  FaEnvelope,
  FaEnvelopeOpen,
  FaWhatsapp,
} from "react-icons/fa";
import "../../css/TelefonosNotificacion.css";
import "../../css/Usuarios.css";
import "../../css/Suscriptores.css";
import "../../css/ConsultaMora.css";

moment.locale("es");

const mapearItem = (item) => ({
  id: item.id_suscripcion,
  documento: item.numero_documento,
  nombresApellidos: item.nombres,
  nombre_plan: item.nombre_plan,
  estado_suscripcion: item.estado_suscripcion,
  estado_cobro: item.estado_cobro,
  valor_adeudado: item.precio_mensual,
  tipo_cobro: item.tipo_cobro,
  fecha_vencimiento: item.fecha_inicio_debito_auto,
  dias_mora: item.dias_en_mora,
  celular: item.celular,
  correo: item.email,
  medio_contacto: item.medio_contacto_preferido,
  total_intentos: item.total_pagos_realizados ?? item.total_intentos,
  historial_errores: item.historial ?? item.historial_errores,
  asesor: item.asesor,
});

const ConsultaMora = () => {
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const [datos, setDatos] = useState([]);
  const [datosFiltrados, setDatosFiltrados] = useState([]);
  const [loading, setLoading] = useState(false);

  const [busqueda, setBusqueda] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [diasMoraMin, setDiasMoraMin] = useState("");
  const [diasMoraMax, setDiasMoraMax] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("");
  const [estadoCobroFiltro, setEstadoCobroFiltro] = useState("");
  const [planFiltro, setPlanFiltro] = useState("");
  const [asesorFiltro, setAsesorFiltro] = useState("");
  const [valorDesde, setValorDesde] = useState("");
  const [valorHasta, setValorHasta] = useState("");

  const [planesUnicos, setPlanesUnicos] = useState([]);
  const [asesoresUnicos, setAsesoresUnicos] = useState([]);
  const [estadosUnicos, setEstadosUnicos] = useState([]);
  const [estadosCobroUnicos, setEstadosCobroUnicos] = useState([]);

  const [paginaActual, setPaginaActual] = useState(1);
  const [ordenColumna, setOrdenColumna] = useState(null);
  const [ordenDireccion, setOrdenDireccion] = useState("asc");
  const elementosPorPagina = 15;

  const [modalHistorial, setModalHistorial] = useState(false);
  const [historialActivo, setHistorialActivo] = useState(null);
  const [modalContacto, setModalContacto] = useState(false);
  const [contactoActivo, setContactoActivo] = useState(null);

  const fetchAfiliados = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fechaInicio) params.fechaInicio = fechaInicio;
      if (fechaFin) params.fechaFin = fechaFin;

      const response = await axios.get(
        `${baseURL}/api/Facturacion/SuscriptoresMora`,
        { params }
      );
      const lista = (response.data || []).map(mapearItem);
      setDatos(lista);

      const planes = [...new Set(lista.map((item) => item.nombre_plan).filter(Boolean))];
      const asesores = [...new Set(lista.map((item) => item.asesor).filter(Boolean))];
      const estados = [...new Set(lista.map((item) => item.estado_suscripcion).filter(Boolean))];
      const estadosCobro = [...new Set(lista.map((item) => item.estado_cobro).filter(Boolean))];
      setPlanesUnicos(planes);
      setAsesoresUnicos(asesores);
      setEstadosUnicos(estados);
      setEstadosCobroUnicos(estadosCobro);
    } catch (error) {
      console.error("Error al cargar suscriptores en mora:", error);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Error al cargar los datos de cartera",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      setLoading(false);
    }
  }, [baseURL, fechaInicio, fechaFin]);

  useEffect(() => {
    fetchAfiliados();
  }, [fetchAfiliados]);

  useEffect(() => {
    const q = busqueda.toLowerCase();
    const filtrados = datos.filter((item) => {
      const coincideTexto =
        !q ||
        item.documento?.toLowerCase().includes(q) ||
        item.nombresApellidos?.toLowerCase().includes(q) ||
        item.celular?.toLowerCase().includes(q) ||
        item.correo?.toLowerCase().includes(q) ||
        item.asesor?.toLowerCase().includes(q) ||
        item.nombre_plan?.toLowerCase().includes(q);

      const dias = Number(item.dias_mora) || 0;
      const coincideDiasMin = !diasMoraMin || dias >= Number(diasMoraMin);
      const coincideDiasMax = !diasMoraMax || dias <= Number(diasMoraMax);

      const coincideEstado =
        !estadoFiltro || item.estado_suscripcion === estadoFiltro;
      const coincideEstadoCobro =
        !estadoCobroFiltro || item.estado_cobro === estadoCobroFiltro;
      const coincidePlan = !planFiltro || item.nombre_plan === planFiltro;
      const coincideAsesor = !asesorFiltro || item.asesor === asesorFiltro;

      const valor = Number(item.valor_adeudado) || 0;
      const coincideValorDesde =
        !valorDesde || valor >= Number(valorDesde);
      const coincideValorHasta =
        !valorHasta || valor <= Number(valorHasta);

      return (
        coincideTexto &&
        coincideDiasMin &&
        coincideDiasMax &&
        coincideEstado &&
        coincideEstadoCobro &&
        coincidePlan &&
        coincideAsesor &&
        coincideValorDesde &&
        coincideValorHasta
      );
    });
    setDatosFiltrados(filtrados);
    setPaginaActual(1);
  }, [
    datos,
    busqueda,
    diasMoraMin,
    diasMoraMax,
    estadoFiltro,
    estadoCobroFiltro,
    planFiltro,
    asesorFiltro,
    valorDesde,
    valorHasta,
  ]);

  const ordenar = (columna) => {
    if (ordenColumna === columna) {
      setOrdenDireccion(ordenDireccion === "asc" ? "desc" : "asc");
    } else {
      setOrdenColumna(columna);
      setOrdenDireccion("asc");
    }
  };

  const datosOrdenados = [...datosFiltrados].sort((a, b) => {
    if (!ordenColumna) return 0;
    const va = a[ordenColumna];
    const vb = b[ordenColumna];
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;

    let cmp = 0;
    if (typeof va === "number" || !isNaN(Number(va))) {
      cmp = Number(va) - Number(vb);
    } else {
      cmp = String(va).localeCompare(String(vb), "es", { sensitivity: "base" });
    }
    return ordenDireccion === "asc" ? cmp : -cmp;
  });

  const totalPaginas = Math.ceil(datosOrdenados.length / elementosPorPagina);
  const indexInicio = (paginaActual - 1) * elementosPorPagina;
  const datosPaginados = datosOrdenados.slice(
    indexInicio,
    indexInicio + elementosPorPagina
  );

  const rangoInicio = Math.max(1, Math.min(paginaActual - 2, totalPaginas - 4));
  const rangoFin = Math.min(totalPaginas, rangoInicio + 4);
  const paginasVisibles = Array.from(
    { length: rangoFin - rangoInicio + 1 },
    (_, i) => rangoInicio + i
  );

  const formatCurrency = (v) =>
    new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }).format(v || 0);

  const badgeEstado = (estado) => {
    const e = estado?.toUpperCase();
    if (e === "ACTIVA") return "badge-activo";
    if (e === "INACTIVA") return "badge-inactivo";
    if (e === "SIN PAGO") return "badge-sinpago";
    if (e === "VENCIDA") return "badge-vencida";
    if (e === "EN MORA") return "badge-mora";
    return "badge-inactivo";
  };

  const badgeEstadoCobro = (estadoCobro) => {
    const e = estadoCobro?.toUpperCase();
    if (e === "EXITOSO") return "badge-exitoso";
    if (e === "EN MORA") return "badge-mora";
    return "badge-inactivo";
  };

  const badgePlan = (plan) => {
    if (!plan) return "badge-plan-basico";
    if (plan.toUpperCase().includes("FAMILIAR")) return "badge-plan-familiar";
    if (plan.toUpperCase().includes("PREMIUM")) return "badge-plan-premium";
    return "badge-plan-basico";
  };

  const limpiarFiltros = () => {
    setBusqueda("");
    setDiasMoraMin("");
    setDiasMoraMax("");
    setEstadoFiltro("");
    setEstadoCobroFiltro("");
    setPlanFiltro("");
    setAsesorFiltro("");
    setValorDesde("");
    setValorHasta("");
  };

  const iconoOrden = (columna) => {
    if (ordenColumna !== columna)
      return <FaSort style={{ opacity: 0.3, marginLeft: 4, fontSize: "0.7rem" }} />;
    if (ordenDireccion === "asc")
      return <FaSortUp style={{ marginLeft: 4, fontSize: "0.7rem" }} />;
    return <FaSortDown style={{ marginLeft: 4, fontSize: "0.7rem" }} />;
  };

  const verHistorial = (item) => {
    setHistorialActivo(item);
    setModalHistorial(true);
  };

  const enviarCorreo = async (item) => {
    const confirm = await Swal.fire({
      title: "Enviar correo de cobro",
      html: `
        <p style="font-size:0.9rem;color:#495057;margin-bottom:6px;">
          Se enviará un recordatorio de pago a:
        </p>
        <div style="background:#f8f9fa;border-radius:8px;padding:10px 14px;text-align:left;">
          <div style="font-weight:600;color:#343a40;">${item.nombresApellidos}</div>
          <div style="font-size:0.82rem;color:#6c757d;">${item.correo || "Sin correo"}</div>
          <div style="font-size:0.82rem;color:#6c757d;margin-top:2px;">${item.nombre_plan}</div>
        </div>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, enviar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#1a56db",
    });
    if (!confirm.isConfirmed) return;

    try {
      await axios.post(`${baseURL}/api/Facturacion/EnviarCorreoMora`, {
        correoSuscriptor: item.correo,
        nombreSuscriptor: item.nombresApellidos,
        nombrePlan: item.nombre_plan,
      });
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: `Correo enviado a ${item.nombresApellidos}`,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error al enviar",
        text: error.response?.data?.mensaje || "No se pudo enviar el correo.",
        confirmButtonColor: "#1a56db",
      });
    }
  };

  const parsearHistorial = (texto) => {
    if (!texto) return [];
    return texto.split("|").map((linea) => {
      const trimmed = linea.trim();
      const intentoMatch = trimmed.match(/^Intento\s+(\d+):\s*(.+?)\s*\(([^)]+)\)\s*$/);
      if (intentoMatch) {
        return {
          intento: Number(intentoMatch[1]),
          mensaje: intentoMatch[2].trim(),
          fecha: intentoMatch[3].trim(),
          exitoso: false,
        };
      }
      const exitoMatch = trimmed.match(/^(.+?)\s*-\s*\$([\d.,]+)\s*-\s*(\w+)\s*\(([^)]+)\)\s*$/);
      if (exitoMatch) {
        return {
          intento: null,
          mensaje: exitoMatch[1].trim(),
          monto: exitoMatch[2].trim(),
          metodo: exitoMatch[3].trim(),
          fecha: exitoMatch[4].trim(),
          exitoso: true,
        };
      }
      return { intento: null, mensaje: trimmed, fecha: null, exitoso: false };
    });
  };

  const exportarExcel = () => {
    if (datosFiltrados.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Sin datos",
        text: "No hay registros para exportar.",
        confirmButtonColor: "#1a56db",
      });
      return;
    }

    const parsearIntentos = (historial) => {
      if (!historial) return "";
      return historial
        .split("|")
        .map((linea) => {
          const trimmed = linea.trim();
          const intentoMatch = trimmed.match(/^Intento\s+(\d+):\s*(.+?)\s*\(([^)]+)\)\s*$/);
          if (intentoMatch) {
            const fecha = moment(intentoMatch[3].trim()).format("DD/MM/YYYY hh:mm A");
            return `Intento ${intentoMatch[1]} [${fecha}]: ${intentoMatch[2].trim()}`;
          }
          const exitoMatch = trimmed.match(/^(.+?)\s*-\s*\$([\d.,]+)\s*-\s*(\w+)\s*\(([^)]+)\)\s*$/);
          if (exitoMatch) {
            const fecha = moment(exitoMatch[4].trim()).format("DD/MM/YYYY hh:mm A");
            return `${exitoMatch[1].trim()} - $${exitoMatch[2]} - ${exitoMatch[3]} [${fecha}]`;
          }
          return trimmed;
        })
        .join(" | ");
    };

    const datosExcel = datosFiltrados.map((item) => ({
      Documento: item.documento || "",
      "Nombre Completo": item.nombresApellidos || "",
      Celular: item.celular || "",
      Correo: item.correo || "",
      "Medio Contacto Preferido": item.medio_contacto || "",
      Plan: item.nombre_plan || "",
      Estado: item.estado_suscripcion || "",
      "Estado Cobro": item.estado_cobro || "",
      "Valor Adeudado": item.valor_adeudado != null ? Number(item.valor_adeudado) : 0,
      "Fecha Venc.": item.fecha_vencimiento ? moment(item.fecha_vencimiento).format("DD/MM/YYYY") : "",
      "Días Mora": item.dias_mora != null ? Number(item.dias_mora) : 0,
      Intentos: item.total_intentos != null ? Number(item.total_intentos) : 0,
      Asesor: item.asesor || "",
      "Historial de Intentos": parsearIntentos(item.historial_errores),
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(datosExcel);

    const anchoColumnas = [
      { wch: 16 },  // Documento
      { wch: 32 },  // Nombre Completo
      { wch: 14 },  // Celular
      { wch: 28 },  // Correo
      { wch: 22 },  // Medio Contacto Preferido
      { wch: 26 },  // Plan
      { wch: 16 },  // Estado
      { wch: 16 },  // Estado Cobro
      { wch: 18 },  // Valor Adeudado
      { wch: 14 },  // Fecha Venc.
      { wch: 12 },  // Días Mora
      { wch: 10 },  // Intentos
      { wch: 22 },  // Asesor
      { wch: 80 },  // Historial de Intentos
    ];
    ws["!cols"] = anchoColumnas;

    XLSX.utils.book_append_sheet(wb, ws, "Cartera");

    XLSX.writeFile(wb, `Cartera_${moment().format("YYYYMMDD_HHmmss")}.xlsx`);

    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "success",
      title: "Archivo exportado correctamente",
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
    });
  };

  return (
    <div className="telefonos-container">
      <div className="telefonos-card">
        {/* ── Header ── */}
        <div className="card-header-custom d-flex align-items-center justify-content-between">
          <h3 className="card-title-custom">
            <FaWallet style={{ marginRight: 10 }} />
            Cartera
          </h3>
          <span className="fact-badge-count">
            {datosFiltrados.length} registro{datosFiltrados.length !== 1 ? "s" : ""}
          </span>
        </div>

        <div className="form-section">
          {/* ── Filtros fila 1 ── */}
          <div className="mora-filtros-wrap">
            <div className="usuarios-search-wrap mora-search">
              <FaSearch className="usuarios-search-icon" />
              <input
                type="text"
                className="custom-input usuarios-search-input"
                placeholder="Buscar por documento, nombre, celular, correo..."
                autoComplete="off"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>

            <div className="mora-filter-item mora-filter-date">
              <label className="mora-filter-label">Venc. Desde</label>
              <input
                type="date"
                className="custom-input mora-date-input"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
              />
            </div>

            <div className="mora-filter-item mora-filter-date">
              <label className="mora-filter-label">Venc. Hasta</label>
              <input
                type="date"
                className="custom-input mora-date-input"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
              />
            </div>

            <div className="mora-filter-item mora-filter-num">
              <label className="mora-filter-label">Días Mora (mín)</label>
              <input
                type="number"
                className="custom-input mora-num-input"
                placeholder="0"
                min="0"
                value={diasMoraMin}
                onChange={(e) => setDiasMoraMin(e.target.value)}
              />
            </div>

            <div className="mora-filter-item mora-filter-num">
              <label className="mora-filter-label">Días Mora (máx)</label>
              <input
                type="number"
                className="custom-input mora-num-input"
                placeholder="999"
                min="0"
                value={diasMoraMax}
                onChange={(e) => setDiasMoraMax(e.target.value)}
              />
            </div>
          </div>

          {/* ── Filtros fila 2 ── */}
          <div className="mora-filtros-wrap mora-filtros-row2">
            <div className="mora-filter-item mora-filter-select">
              <label className="mora-filter-label">Estado</label>
              <select
                className="custom-input mora-select-input"
                value={estadoFiltro}
                onChange={(e) => setEstadoFiltro(e.target.value)}
              >
                <option value="">Todos</option>
                {estadosUnicos.map((e) => (
                  <option key={e} value={e}>{e}</option>
                ))}
              </select>
            </div>

            <div className="mora-filter-item mora-filter-select">
              <label className="mora-filter-label">Estado Cobro</label>
              <select
                className="custom-input mora-select-input"
                value={estadoCobroFiltro}
                onChange={(e) => setEstadoCobroFiltro(e.target.value)}
              >
                <option value="">Todos</option>
                {estadosCobroUnicos.map((e) => (
                  <option key={e} value={e}>{e}</option>
                ))}
              </select>
            </div>

            <div className="mora-filter-item mora-filter-select">
              <label className="mora-filter-label">Plan</label>
              <select
                className="custom-input mora-select-input"
                value={planFiltro}
                onChange={(e) => setPlanFiltro(e.target.value)}
              >
                <option value="">Todos</option>
                {planesUnicos.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            <div className="mora-filter-item mora-filter-select">
              <label className="mora-filter-label">Asesor</label>
              <select
                className="custom-input mora-select-input"
                value={asesorFiltro}
                onChange={(e) => setAsesorFiltro(e.target.value)}
              >
                <option value="">Todos</option>
                {asesoresUnicos.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>

            <div className="mora-filter-item mora-filter-num">
              <label className="mora-filter-label">Valor Desde ($)</label>
              <input
                type="number"
                className="custom-input mora-num-input"
                placeholder="0"
                min="0"
                value={valorDesde}
                onChange={(e) => setValorDesde(e.target.value)}
              />
            </div>

            <div className="mora-filter-item mora-filter-num">
              <label className="mora-filter-label">Valor Hasta ($)</label>
              <input
                type="number"
                className="custom-input mora-num-input"
                placeholder="99999999"
                min="0"
                value={valorHasta}
                onChange={(e) => setValorHasta(e.target.value)}
              />
            </div>

            <button className="btn-limpiar-filtros" onClick={limpiarFiltros}>
              <FaFilter style={{ marginRight: 5 }} /> Limpiar
            </button>

            <button className="mora-btn-excel" onClick={exportarExcel}>
              <FaFileExcel style={{ marginRight: 6 }} /> Descargar Excel
            </button>
          </div>

          {/* ── Tabla ── */}
          <div className="table-responsive usuarios-table-wrap mora-table-wrap">
            <table className="usuarios-table mora-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th onClick={() => ordenar("documento")} className="col-sort">
                    Documento {iconoOrden("documento")}
                  </th>
                  <th onClick={() => ordenar("nombresApellidos")} className="col-sort">
                    Nombre {iconoOrden("nombresApellidos")}
                  </th>
                  <th onClick={() => ordenar("nombre_plan")} className="col-sort">
                    Plan {iconoOrden("nombre_plan")}
                  </th>
                  <th onClick={() => ordenar("estado_suscripcion")} className="col-sort">
                    Estado {iconoOrden("estado_suscripcion")}
                  </th>
                  <th onClick={() => ordenar("estado_cobro")} className="col-sort">
                    Cobro {iconoOrden("estado_cobro")}
                  </th>
                  <th onClick={() => ordenar("valor_adeudado")} className="col-sort">
                    Valor {iconoOrden("valor_adeudado")}
                  </th>
                  <th onClick={() => ordenar("fecha_vencimiento")} className="col-sort">
                    Fecha Venc. {iconoOrden("fecha_vencimiento")}
                  </th>
                  <th onClick={() => ordenar("dias_mora")} className="col-sort">
                    Días Mora {iconoOrden("dias_mora")}
                  </th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="sin-resultados">
                      <i className="fas fa-spinner fa-spin mr-2" /> Cargando
                      afiliados...
                    </td>
                  </tr>
                ) : datosPaginados.length > 0 ? (
                  datosPaginados.map((item, idx) => (
                    <tr key={item.id || idx}>
                      <td className="td-id">{indexInicio + idx + 1}</td>
                      <td>
                        <span className="mora-documento">{item.documento}</span>
                      </td>
                      <td>
                        <span className="mora-nombre">{item.nombresApellidos}</span>
                      </td>
                      <td>
                        <span className={`badge-plan-tag ${badgePlan(item.nombre_plan)}`}>
                          {item.nombre_plan}
                        </span>
                      </td>
                      <td>
                        <span className={`badge-estado ${badgeEstado(item.estado_suscripcion)}`}>
                          <span className="badge-dot" />
                          {item.estado_suscripcion}
                        </span>
                      </td>
                      <td>
                        <span className={`badge-estado ${badgeEstadoCobro(item.estado_cobro)}`}>
                          <span className="badge-dot" />
                          {item.estado_cobro}
                        </span>
                      </td>
                      <td>
                        <span className="mora-valor">
                          {formatCurrency(item.valor_adeudado)}
                        </span>
                      </td>
                      <td>
                        {item.fecha_vencimiento
                          ? moment(item.fecha_vencimiento).format("DD/MM/YYYY")
                          : "—"}
                      </td>
                      <td>
                        <span className={`mora-dias ${
                          Number(item.dias_mora) > 90 ? "mora-dias-critico"
                          : Number(item.dias_mora) > 30 ? "mora-dias-alerta" : ""
                        }`}>
                          {item.dias_mora ?? "—"}
                        </span>
                      </td>
                      <td>
                        <div className="acciones-wrap">
                          <button
                            className="btn-icono btn-icono-detalle"
                            onClick={() => {
                              setContactoActivo(item);
                              setModalContacto(true);
                            }}
                            title="Ver datos de contacto"
                          >
                            <FaAddressBook size={14} />
                            <span className="icono-tooltip">Contacto</span>
                          </button>
                          {item.historial_errores && (
                            <button
                              className="btn-icono btn-icono-benef"
                              onClick={() => verHistorial(item)}
                              title="Ver historial de errores"
                            >
                              <FaHistory size={14} />
                              <span className="icono-tooltip">Historial de Pago</span>
                            </button>
                          )}
                          {item.correo && item.estado_cobro !== "EXITOSO" && (
                            <button
                              className="btn-icono btn-icono-pago"
                              onClick={() => enviarCorreo(item)}
                              title="Enviar correo de cobro"
                            >
                              <FaEnvelopeOpen size={14} />
                              <span className="icono-tooltip">Enviar Correo</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="sin-resultados">
                      <i className="fas fa-search mr-2" />
                      No se encontraron registros en cartera
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Paginación ── */}
          {totalPaginas > 1 && (
            <div className="fact-paginacion">
              <span className="fact-pag-info">
                Mostrando {indexInicio + 1}–
                {Math.min(indexInicio + elementosPorPagina, datosOrdenados.length)}{" "}
                de {datosOrdenados.length} registros
              </span>
              <div className="paginacion-wrap">
                <button className="btn-pagina btn-pag-nav" disabled={paginaActual === 1} onClick={() => setPaginaActual(1)} title="Primera">«</button>
                <button className="btn-pagina btn-pag-nav" disabled={paginaActual === 1} onClick={() => setPaginaActual((p) => p - 1)} title="Anterior">‹</button>
                {rangoInicio > 1 && <span className="btn-pagina btn-pag-dots">…</span>}
                {paginasVisibles.map((n) => (
                  <button key={n} className={`btn-pagina ${paginaActual === n ? "activa" : ""}`} onClick={() => setPaginaActual(n)}>{n}</button>
                ))}
                {rangoFin < totalPaginas && <span className="btn-pagina btn-pag-dots">…</span>}
                <button className="btn-pagina btn-pag-nav" disabled={paginaActual === totalPaginas} onClick={() => setPaginaActual((p) => p + 1)} title="Siguiente">›</button>
                <button className="btn-pagina btn-pag-nav" disabled={paginaActual === totalPaginas} onClick={() => setPaginaActual(totalPaginas)} title="Última">»</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Modal Datos de Contacto ── */}
      {modalContacto && contactoActivo && (
        <div className="modal-overlay" onClick={() => setModalContacto(false)}>
          <div className="mora-modal-contacto" onClick={(e) => e.stopPropagation()}>
            <div className="mora-historial-header">
              <div className="mora-historial-header-left">
                <div className="mora-historial-avatar">
                  {(contactoActivo.nombresApellidos || "U").charAt(0).toUpperCase()}
                </div>
                <div>
                  <h5 className="mora-historial-nombre">{contactoActivo.nombresApellidos}</h5>
                  <span className="mora-historial-doc">
                    <FaCreditCard style={{ marginRight: 5, opacity: 0.6, fontSize: "0.7rem" }} />
                    {contactoActivo.documento}
                  </span>
                </div>
              </div>
              <button className="modal-close" onClick={() => setModalContacto(false)}>
                <FaTimes size={14} />
              </button>
            </div>

            <div className="mora-historial-body">
              <div className="mora-contacto-grid">
                <div className="mora-contacto-item">
                  <div className="mora-contacto-icon mora-contacto-icon-cel">
                    <FaPhoneAlt />
                  </div>
                  <div className="mora-contacto-info">
                    <span className="mora-contacto-label">Celular</span>
                    <span className="mora-contacto-valor">{contactoActivo.celular || "—"}</span>
                  </div>
                </div>
                <div className="mora-contacto-item">
                  <div className="mora-contacto-icon mora-contacto-icon-mail">
                    <FaEnvelope />
                  </div>
                  <div className="mora-contacto-info">
                    <span className="mora-contacto-label">Correo Electrónico</span>
                    <span className="mora-contacto-valor">{contactoActivo.correo || "—"}</span>
                  </div>
                </div>
                <div className="mora-contacto-item">
                  <div className="mora-contacto-icon mora-contacto-icon-wsp">
                    <FaWhatsapp />
                  </div>
                  <div className="mora-contacto-info">
                    <span className="mora-contacto-label">Medio de Contacto Preferido</span>
                    <span className="mora-contacto-valor">{contactoActivo.medio_contacto || "—"}</span>
                  </div>
                </div>
                <div className="mora-contacto-item">
                  <div className="mora-contacto-icon mora-contacto-icon-plan">
                    <FaCreditCard />
                  </div>
                  <div className="mora-contacto-info">
                    <span className="mora-contacto-label">Plan Contratado</span>
                    <span className="mora-contacto-valor">{contactoActivo.nombre_plan}</span>
                  </div>
                </div>
              </div>

              <div className="mora-contacto-detalle">
                <div className="mora-contacto-detalle-item">
                  <span className="mora-contacto-detalle-label">Tipo de Cobro</span>
                  <span className="badge-tipo-cobro">{contactoActivo.tipo_cobro || "—"}</span>
                </div>
                <div className="mora-contacto-detalle-item">
                  <span className="mora-contacto-detalle-label">Estado Suscripción</span>
                  <span className={`badge-estado ${badgeEstado(contactoActivo.estado_suscripcion)}`}>
                    <span className="badge-dot" />{contactoActivo.estado_suscripcion}
                  </span>
                </div>
                <div className="mora-contacto-detalle-item">
                  <span className="mora-contacto-detalle-label">Estado Cobro</span>
                  <span className={`badge-estado ${badgeEstadoCobro(contactoActivo.estado_cobro)}`}>
                    <span className="badge-dot" />{contactoActivo.estado_cobro}
                  </span>
                </div>
                <div className="mora-contacto-detalle-item">
                  <span className="mora-contacto-detalle-label">Asesor</span>
                  <span className="mora-contacto-detalle-valor">{contactoActivo.asesor || "—"}</span>
                </div>
                <div className="mora-contacto-detalle-item">
                  <span className="mora-contacto-detalle-label">Valor Adeudado</span>
                  <span className="mora-contacto-detalle-valor mora-valor">
                    {formatCurrency(contactoActivo.valor_adeudado)}
                  </span>
                </div>
              </div>
            </div>

            <div className="modal-footer-custom">
              <button className="btn-modal-cancelar" onClick={() => setModalContacto(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Historial de Errores ── */}
      {modalHistorial && historialActivo && (() => {
        const intentos = parsearHistorial(historialActivo.historial_errores).reverse();
        return (
        <div className="modal-overlay" onClick={() => setModalHistorial(false)}>
          <div className="mora-modal-historial" onClick={(e) => e.stopPropagation()}>
            {/* ── Header ── */}
            <div className="mora-historial-header">
              <div className="mora-historial-header-left">
                <div className="mora-historial-avatar">
                  {(historialActivo.nombresApellidos || "U").charAt(0).toUpperCase()}
                </div>
                <div>
                  <h5 className="mora-historial-nombre">{historialActivo.nombresApellidos}</h5>
                  <span className="mora-historial-doc">
                    <FaCreditCard style={{ marginRight: 5, opacity: 0.6, fontSize: "0.7rem" }} />
                    {historialActivo.documento}
                  </span>
                </div>
              </div>
              <button className="modal-close" onClick={() => setModalHistorial(false)}>
                <FaTimes size={14} />
              </button>
            </div>

            {/* ── Resumen en grid 2x2 ── */}
            <div className="mora-historial-body">
              <div className="mora-resumen-grid">
                <div className="mora-resumen-card">
                  <div className="mora-resumen-card-icon mora-rc-icon-plan">
                    <FaCreditCard />
                  </div>
                  <div className="mora-resumen-card-info">
                    <span className="mora-rc-label">Plan</span>
                    <span className="mora-rc-value">{historialActivo.nombre_plan}</span>
                  </div>
                </div>
                <div className="mora-resumen-card">
                  <div className="mora-resumen-card-icon mora-rc-icon-cobro">
                    <FaExclamationCircle />
                  </div>
                  <div className="mora-resumen-card-info">
                    <span className="mora-rc-label">Tipo de Cobro</span>
                    <span className="mora-rc-value">{historialActivo.tipo_cobro || "—"}</span>
                  </div>
                </div>
                <div className="mora-resumen-card">
                  <div className="mora-resumen-card-icon mora-rc-icon-valor">
                    <FaExclamationTriangle />
                  </div>
                  <div className="mora-resumen-card-info">
                    <span className="mora-rc-label">Valor a Cobrar</span>
                    <span className="mora-rc-value mora-rc-value-highlight">
                      {formatCurrency(historialActivo.valor_adeudado)}
                    </span>
                  </div>
                </div>
                <div className="mora-resumen-card">
                  <div className="mora-resumen-card-icon mora-rc-icon-intentos">
                    {historialActivo.estado_cobro === "EXITOSO" ? <FaCheckCircle /> : <FaTimesCircle />}
                  </div>
                  <div className="mora-resumen-card-info">
                    <span className="mora-rc-label">{historialActivo.estado_cobro === "EXITOSO" ? "Pagos Realizados" : "Intentos Fallidos"}</span>
                    <span className={`mora-rc-value ${Number(historialActivo.total_intentos) >= 4 && historialActivo.estado_cobro !== "EXITOSO" ? "mora-rc-value-critico" : ""}`}>
                      {historialActivo.total_intentos}
                    </span>
                  </div>
                </div>
              </div>

              {/* ── Timeline ── */}
              <div className="mora-timeline-wrapper">
                <h6 className="mora-timeline-titulo">
                  <FaHistory style={{ marginRight: 6 }} />
                  Cronología de pagos
                </h6>

                <div className="mora-timeline">
                  {intentos.map((int, idx) => {
                    const diasDesde = int.fecha ? moment(int.fecha).fromNow() : null;
                    const esExitoso = int.exitoso;
                    const numSecuencia = intentos.length - idx;
                    return (
                    <div key={idx} className={`mora-timeline-item ${idx === intentos.length - 1 ? "mora-timeline-ultimo" : ""}`}>
                      <div className="mora-timeline-indicador">
                        <div className={`mora-timeline-circulo ${esExitoso ? "mora-timeline-circulo-exitoso" : ""}`}>
                          <span className="mora-timeline-numero">{numSecuencia}</span>
                        </div>
                        {idx < intentos.length - 1 && (
                          <div className={`mora-timeline-linea ${esExitoso ? "mora-timeline-linea-exitoso" : ""}`} />
                        )}
                      </div>
                      <div className="mora-timeline-contenido">
                        <div className="mora-timeline-card">
                          <div className="mora-timeline-card-header">
                            <span className="mora-timeline-titulo-item">
                              {esExitoso ? `Pago #${numSecuencia}` : `Intento #${numSecuencia}`}
                            </span>
                            {int.fecha && (
                              <span className="mora-timeline-fecha">
                                <FaCalendarAlt style={{ marginRight: 5, fontSize: "0.65rem" }} />
                                {moment(int.fecha).format("DD/MM/YYYY")}
                                <span className="mora-timeline-hora">
                                  {moment(int.fecha).format("hh:mm A")}
                                </span>
                              </span>
                            )}
                          </div>
                          <div className="mora-timeline-mensaje">
                            {esExitoso ? (
                              <FaCheckCircle className="mora-timeline-icono-exito" />
                            ) : (
                              <FaTimesCircle className="mora-timeline-icono-error" />
                            )}
                            <span>{int.mensaje}</span>
                          </div>
                          {esExitoso && int.monto && (
                            <div className="mora-timeline-monto">
                              {formatCurrency(Number(int.monto))} · {int.metodo}
                            </div>
                          )}
                          {diasDesde && (
                            <div className="mora-timeline-dias">
                              {diasDesde}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )})}
                </div>

                {intentos.length === 0 && (
                  <div className="sin-resultados" style={{ padding: "1.5rem" }}>
                    No hay historial de pagos disponible.
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer-custom">
              <button className="btn-modal-cancelar" onClick={() => setModalHistorial(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default ConsultaMora;
